import { dictionaryApi } from './dictionaryClient';
import { type AnubrittiLinkSutra, normalizeAnubrittiLinkSutra } from './ashtadhyayiApi';

export type BartikAnubrittiPadachhedRef = {
  id: number;
  ashtadhyayiBartikId: number;
  order: number;
  text: string;
  bibhakti: number;
  bachan: number;
  digits: string | null;
};

export type BartikAnubritti = {
  id: number;
  ashtadhyayiBartikId: number;
  order: number;
  text: string;
  link: number | null;
  linkSutra: AnubrittiLinkSutra | null;
  ashtadhyayiBartikPadachhedId: number | null;
  ashtadhyayiBartikPadachhed: BartikAnubrittiPadachhedRef | null;
};

export type CreateBartikAnubrittiBody = {
  ashtadhyayiBartikId: number;
  order: number;
  text: string;
  link?: number | string | null;
  l?: number | string | null;
  ashtadhyayiBartikPadachhedId?: number | null;
};

export type UpdateBartikAnubrittiBody = Partial<Omit<CreateBartikAnubrittiBody, 'ashtadhyayiBartikId'>>;

function isRecord(x: unknown): x is Record<string, unknown> {
  return x != null && typeof x === 'object' && !Array.isArray(x);
}

function unwrapEntityPayload(raw: unknown): unknown {
  if (isRecord(raw) && 'data' in raw) return raw.data;
  return raw;
}

function num(o: Record<string, unknown>, key: string): number | null {
  const v = o[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
}

function str(o: Record<string, unknown>, key: string): string {
  if (!(key in o) || o[key] == null) return '';
  return String(o[key]);
}

function normalizePadachhedRef(raw: unknown): BartikAnubrittiPadachhedRef | null {
  if (!isRecord(raw)) return null;
  const id = num(raw, 'id');
  const ashtadhyayiBartikId = num(raw, 'ashtadhyayiBartikId') ?? num(raw, 'ashtadhyayi_bartik_id');
  const order = num(raw, 'order');
  const bibhakti = num(raw, 'bibhakti');
  const bachan = num(raw, 'bachan');
  if (id == null || ashtadhyayiBartikId == null || order == null || bibhakti == null || bachan == null) {
    return null;
  }
  const digitsRaw = raw.digits;
  return {
    id,
    ashtadhyayiBartikId,
    order,
    text: str(raw, 'text'),
    bibhakti,
    bachan,
    digits: digitsRaw == null ? null : String(digitsRaw),
  };
}

export function normalizeBartikAnubritti(raw: unknown): BartikAnubritti | null {
  if (!isRecord(raw)) return null;
  const id = num(raw, 'id');
  const ashtadhyayiBartikId = num(raw, 'ashtadhyayiBartikId') ?? num(raw, 'ashtadhyayi_bartik_id');
  const order = num(raw, 'order');
  if (id == null || ashtadhyayiBartikId == null || order == null) return null;
  const linkSutraRaw = raw.linkSutra ?? raw.link_sutra;
  const linkSutra = linkSutraRaw ? normalizeAnubrittiLinkSutra(linkSutraRaw) : null;
  const link = num(raw, 'link') ?? num(raw, 'l') ?? num(raw, 'sutra') ?? linkSutra?.id ?? null;
  const padachhedRaw = raw.ashtadhyayiBartikPadachhed ?? raw.ashtadhyayi_bartik_padachhed;
  const ashtadhyayiBartikPadachhed = padachhedRaw ? normalizePadachhedRef(padachhedRaw) : null;
  const ashtadhyayiBartikPadachhedId =
    num(raw, 'ashtadhyayiBartikPadachhedId') ??
    num(raw, 'ashtadhyayi_bartik_padachhed_id') ??
    ashtadhyayiBartikPadachhed?.id ??
    null;
  return {
    id,
    ashtadhyayiBartikId,
    order,
    text: str(raw, 'text'),
    link,
    linkSutra,
    ashtadhyayiBartikPadachhedId,
    ashtadhyayiBartikPadachhed,
  };
}

function normalizeList(data: unknown): BartikAnubritti[] {
  const rows =
    Array.isArray(data) ? data : isRecord(data) && Array.isArray(data.data) ? (data.data as unknown[]) : [];
  return rows
    .map(normalizeBartikAnubritti)
    .filter((x): x is BartikAnubritti => x != null)
    .sort((a, b) => a.order - b.order);
}

export async function listBartikAnubritti(ashtadhyayiBartikId?: number): Promise<BartikAnubritti[]> {
  const params =
    ashtadhyayiBartikId != null && Number.isFinite(ashtadhyayiBartikId)
      ? { ashtadhyayiBartikId }
      : undefined;
  const res = await dictionaryApi.get<unknown>('/ashtadhyayi-bartik-anubritti', { params });
  return normalizeList(res.data);
}

export async function getBartikAnubritti(id: number): Promise<BartikAnubritti> {
  const res = await dictionaryApi.get<unknown>(`/ashtadhyayi-bartik-anubritti/${id}`);
  const row = normalizeBartikAnubritti(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid bartik anubritti response');
  return row;
}

export async function createBartikAnubritti(body: CreateBartikAnubrittiBody): Promise<BartikAnubritti> {
  const res = await dictionaryApi.post<unknown>('/ashtadhyayi-bartik-anubritti', body);
  const row = normalizeBartikAnubritti(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid bartik anubritti response');
  return row;
}

export async function updateBartikAnubritti(
  id: number,
  body: UpdateBartikAnubrittiBody
): Promise<BartikAnubritti> {
  const res = await dictionaryApi.patch<unknown>(`/ashtadhyayi-bartik-anubritti/${id}`, body);
  const row = normalizeBartikAnubritti(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid bartik anubritti response');
  return row;
}

export async function deleteBartikAnubritti(id: number): Promise<void> {
  await dictionaryApi.delete(`/ashtadhyayi-bartik-anubritti/${id}`);
}
